import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Pencil, Languages, Brain, Flame, ArrowRight, TrendingUp, ClipboardList, RotateCcw, MessageCircle, Sparkles, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import CountdownBOE from "../components/CountdownBOE";
import { Button } from "../components/ui/button";

const ICONS = { BookOpen, Pencil, Languages, Brain };

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-ink-800 border border-white/5 rounded-2xl p-5 hover:border-white/10 transition-colors">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">{label}</div>
      <div className={`mt-2 font-display text-3xl lg:text-4xl font-extrabold tracking-tighter ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, premium } = useAuth();
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [failed, setFailed] = useState(null);
  const [examConfig, setExamConfig] = useState(null);
  const [daily, setDaily] = useState(null);
  const [dailyStreak, setDailyStreak] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(console.error);
    api.get("/stats").then((r) => setStats(r.data)).catch(console.error);
    api.get("/history?limit=5").then((r) => setHistory(r.data)).catch(console.error);
    api.get("/failed-questions/count").then((r) => setFailed(r.data)).catch(console.error);
    api.get("/exam-config").then((r) => setExamConfig(r.data)).catch(console.error);
    api.get("/daily-challenge").then((r) => setDaily(r.data)).catch(console.error);
    api.get("/daily-challenge/streak").then((r) => setDailyStreak(r.data)).catch(console.error);
  }, []);

  return (
    <div className="min-h-screen bg-ink relative">
      <SEO title="Panel" path="/dashboard" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="fixed inset-0 bg-dot-grid pointer-events-none opacity-30" />

      <div className="relative">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          {/* Greeting */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono">Panel del opositor</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[0.95]" data-testid="dashboard-greeting">
                Hola, <span className="text-gcgold">{user?.name?.split(" ")[0] || "Opositor"}</span>
              </h1>
              <p className="mt-2 text-base text-slate-400">Cada test te acerca a tu plaza.</p>
            </div>
            <Button asChild className="bg-gcgold hover:bg-gcgold-light text-ink font-bold h-12 px-6 rounded-xl shadow-[0_0_40px_-8px_rgba(212,175,55,0.6)] group" data-testid="dashboard-start-test">
              <Link to="/tests">Empezar test <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" /></Link>
            </Button>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard label="Tests" value={stats?.total_tests ?? 0} />
            <StatCard label="Nota media" value={stats?.avg_score ?? 0} accent="text-gcgold" />
            <StatCard label="Precisión" value={`${stats?.accuracy ?? 0}%`} />
            <StatCard label="Racha" value={`${stats?.streak_days ?? 0}d`} accent="text-emerald-400" />
          </div>

          {/* Premium banner */}
          {premium && !premium.is_premium && (
            <Link
              to="/premium"
              className="block relative overflow-hidden rounded-2xl border border-gcgold/40 bg-gradient-to-r from-gcgold/15 via-ink-800 to-ink-800 p-5 mb-6 group hover:border-gcgold/70 transition-all"
              data-testid="dashboard-premium-banner"
            >
              <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-gcgold/20 blur-3xl" />
              <div className="relative flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-gcgold/20 flex items-center justify-center flex-shrink-0">
                  <Crown className="h-6 w-6 text-gcgold" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-lg font-bold text-white">
                    {premium.trial_used ? "Hazte Premium · desde 5,50€/mes" : `Prueba Premium 7 días gratis`}
                  </div>
                  <div className="text-sm text-slate-400 mt-0.5">
                    Simulacros oficiales históricos · estadísticas avanzadas · sin límites
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gcgold group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          )}
          {premium && premium.is_premium && (
            <div className="rounded-2xl border border-gcgold/40 bg-gcgold/5 p-4 mb-6 flex items-center gap-3" data-testid="dashboard-premium-active">
              <Crown className="h-5 w-5 text-gcgold" />
              <div className="text-sm text-slate-200">
                <strong className="text-gcgold">Premium {premium.is_trial ? "(Prueba)" : "activo"}</strong> · {premium.days_left} días restantes
              </div>
            </div>
          )}

          {/* BOE Countdown */}
          {examConfig && (
            <div className="mb-6">
              <CountdownBOE config={examConfig} />
            </div>
          )}

          {/* Daily challenge banner */}
          {daily && (
            <Link
              to="/reto-diario"
              className={`block relative overflow-hidden rounded-2xl border p-6 mb-10 group transition-all hover:-translate-y-0.5 ${
                daily.completed
                  ? "bg-ink-800 border-emerald-500/40"
                  : "bg-gradient-to-br from-gcgold/10 via-ink-800 to-ink-700 border-gcgold/40 shadow-[0_0_60px_-15px_rgba(212,175,55,0.4)]"
              }`}
              data-testid="daily-banner"
            >
              <div className="absolute inset-0 bg-dot-grid-dense opacity-20" />
              <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-gcgold/15 blur-3xl" />
              <div className="relative flex flex-wrap items-center gap-6">
                <div className={`h-14 w-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  daily.completed ? "bg-emerald-500/15 border border-emerald-400/30" : "bg-gcgold/15 border border-gcgold/40"
                }`}>
                  {daily.completed ? <CheckCircle2 className="h-6 w-6 text-emerald-400" /> : <Sparkles className="h-6 w-6 text-gcgold animate-pulse" />}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.22em] font-mono mb-1 ${daily.completed ? "text-emerald-400" : "text-gcgold"}`}>
                    {daily.completed ? "Reto completado" : "Reto diario · Disponible"}
                  </div>
                  <div className="font-display text-2xl font-extrabold text-white tracking-tight">
                    {daily.completed
                      ? `Has sacado ${daily.attempt?.score_10}/10 hoy`
                      : "10 preguntas, 10 minutos, 1 oportunidad"}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {daily.completed
                      ? `${daily.participants_today} opositores ya lo han hecho hoy`
                      : `${daily.participants_today} opositores ya lo han intentado · Vuelve mañana si lo completas`}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {dailyStreak && dailyStreak.current_streak > 0 && (
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-400 font-mono">Racha</div>
                      <div className="flex items-baseline gap-1 justify-end">
                        <span className="font-display text-2xl font-extrabold text-rose-400 tracking-tighter">{dailyStreak.current_streak}</span>
                        <Flame className="h-4 w-4 text-rose-400" />
                      </div>
                    </div>
                  )}
                  <ArrowRight className="h-5 w-5 text-gcgold group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          )}

          {/* Main grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Categories */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-bold text-white tracking-tight">Categorías</h2>
                <Link to="/tests" className="text-xs font-mono font-bold text-gcgold hover:text-gcgold-light uppercase tracking-wider">Ver todas →</Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categories.map((c, i) => {
                  const Icon = ICONS[c.icon] || BookOpen;
                  return (
                    <motion.div key={c.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}>
                      <Link
                        to={`/tests/${c.id}`}
                        className="block relative overflow-hidden bg-ink-800 hover:bg-ink-700 border border-white/5 hover:border-gcgold/30 rounded-2xl p-5 transition-all hover:-translate-y-0.5 group"
                        data-testid={`dashboard-cat-${c.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 group-hover:border-gcgold/40 group-hover:bg-gcgold/10 flex items-center justify-center transition-all">
                            <Icon className="h-5 w-5 text-gcgold" strokeWidth={1.5} />
                          </div>
                          <span className="text-[10px] font-mono text-slate-500 font-bold uppercase tracking-wider">{c.question_count} preg</span>
                        </div>
                        <h3 className="font-display text-xl font-bold text-white tracking-tight">{c.name}</h3>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</p>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-3">
              <Link
                to="/comunidad"
                className="block relative overflow-hidden bg-ink-800 border border-emerald-500/30 rounded-2xl p-5 hover:-translate-y-0.5 transition-all group"
                data-testid="dashboard-community-card"
              >
                <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-emerald-500/20 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="h-3.5 w-3.5 text-emerald-400" />
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-400 font-mono">Comunidad</div>
                    <span className="ml-auto flex h-2 w-2"><span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-70" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
                  </div>
                  <div className="font-display text-xl font-bold text-white">Chat en vivo</div>
                  <p className="text-sm text-slate-400 mt-1.5">Resuelve dudas y comparte con otros opositores en tiempo real.</p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                    Entrar al chat <ArrowRight className="h-3 w-3" />
                  </div>
                </div>
              </Link>

              {failed && failed.total > 0 && (
                <Link
                  to="/tests/failed"
                  className="block relative overflow-hidden bg-ink-800 border border-gcgold/30 rounded-2xl p-5 hover:-translate-y-0.5 transition-all group"
                  data-testid="dashboard-failed-card"
                >
                  <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-rose-500/20 blur-2xl" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <RotateCcw className="h-3.5 w-3.5 text-gcgold" />
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono">Repaso</div>
                    </div>
                    <div className="font-display text-4xl font-extrabold text-white tracking-tighter">{failed.total}</div>
                    <div className="text-sm text-slate-400 mt-1">preguntas falladas</div>
                    <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono font-bold text-gcgold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                      Repasar errores <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              )}

              <div className="relative overflow-hidden bg-gradient-to-br from-gcgreen via-ink-700 to-ink-800 rounded-2xl p-5 border border-gcgreen/30">
                <div className="absolute inset-0 bg-dot-grid opacity-30" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <Flame className="h-3.5 w-3.5 text-gcgold" />
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono">Hoy</div>
                  </div>
                  <div className="font-display text-xl font-bold text-white">Mantén tu racha</div>
                  <p className="text-sm text-slate-300 mt-1.5">Haz al menos 1 test al día para subir en el ranking.</p>
                  <Button asChild className="mt-4 bg-gcgold hover:bg-gcgold-light text-ink font-bold w-full rounded-xl" data-testid="dashboard-simulacro">
                    <Link to="/tests">Hacer simulacro</Link>
                  </Button>
                </div>
              </div>

            </div>
          </div>

          {/* Activity */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-ink-800 border border-white/5 rounded-2xl">
              <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                <h2 className="font-display text-base font-bold text-white flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-gcgold" /> Últimos tests
                </h2>
                <Link to="/stats" className="text-xs font-mono font-bold text-gcgold hover:text-gcgold-light uppercase tracking-wider">Ver todo →</Link>
              </div>
              <div className="divide-y divide-white/5" data-testid="dashboard-history">
                {history.length === 0 && <div className="p-6 text-sm text-slate-500">Aún no has realizado ningún test. ¡Empieza ya!</div>}
                {history.map((h) => (
                  <div key={h.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                    <div>
                      <div className="text-sm font-medium text-white">{h.category_name}</div>
                      <div className="text-xs text-slate-500 font-mono">
                        {new Date(h.submitted_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {h.mode}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-2xl font-extrabold text-gcgold tracking-tighter">{h.score_10}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{h.correct}/{h.total}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-ink-800 border border-white/5 rounded-2xl">
              <div className="px-5 py-4 border-b border-white/5">
                <h2 className="font-display text-base font-bold text-white flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-gcgold" /> Progreso por categoría
                </h2>
              </div>
              <div className="p-5 space-y-4">
                {(stats?.by_category || []).length === 0 && <div className="text-sm text-slate-500">Realiza tu primer test para ver tu progreso.</div>}
                {(stats?.by_category || []).map((c) => (
                  <div key={c.category_id}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="font-medium text-slate-300">{c.category_name}</span>
                      <span className="font-mono text-xs text-gcgold font-bold">{c.avg_score} / 10</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-gcgreen to-gcgold rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, (c.avg_score / 10) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
